import { Flex, useMantineTheme, Modal, Button, Text, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { InfoCircle } from "iconsax-react";
import TextHTML from "../TextHTML";
import {
  formatUtcDateOnly,
  formatTimeOnly,
  meetingDate,
  parseExpireDate,
} from "../../utils/date";
import {
  ETypeInstrument,
  ETypePredictive,
  EValidityType,
  IPostPredictiveOrder,
  IPredictiveProduct,
  IPredictiveProductInstrument,
} from "../../hooks/usePredictives";
import { formatCurrency } from "../../utils/currency";

interface IProps {
  onClose: () => void;
  opened: boolean;
  onConfirm: () => void;
  data: IPredictiveProduct | undefined;
  selectedInstrument: IPredictiveProductInstrument | null;
  watch: IPostPredictiveOrder;
  valueTransaction: number;
}

export default function ConfirmModal({
  onClose,
  opened,
  onConfirm,
  data,
  selectedInstrument,
  watch,
  valueTransaction,
}: IProps) {
  const { t } = useTranslation();
  const sideInstrument = watch.side;
  const isSideSell = sideInstrument === ETypeInstrument.SELL;
  const theme = useMantineTheme();
  const isLimit = watch.type === ETypePredictive.LIMIT;
  const displayParsed = parseExpireDate(watch.expire_date);
  const displayDate = formatUtcDateOnly(displayParsed.date);
  const displayTime = formatTimeOnly(displayParsed.hour, displayParsed.minute);

  const dateExpiration =
    watch.validity === EValidityType.UNTIL_DATE
      ? t(
          "global.predictives_buy_card.modal_confirm.date_expiration.until_cancel",
          {
            date: `${displayDate} ${displayTime}`,
          }
        )
      : watch.validity === EValidityType.UNTIL_CANCEL
      ? t(
          "global.predictives_buy_card.modal_confirm.date_expiration.until_date"
        )
      : t("global.predictives_buy_card.modal_confirm.date_expiration.today");
  return (
    <Modal
      size="auto"
      opened={opened}
      onClose={onClose}
      closeOnClickOutside={false}
      transitionProps={{
        exitDuration: 600,
        transition: "slide-up",
        duration: 600,
        timingFunction: "ease",
      }}
      centered
      styles={{
        content: {
          background: theme.colors.primaryOpacity[1],
          borderRadius: "12px",
        },
        inner: { zIndex: 1004 },
        overlay: { zIndex: 1003 },
        header: {
          marginBottom: "0",
          background: "transparent",
          height: "75px",
        },
        close: {
          display: "none",
          position: "absolute",
          top: "12px",
          right: "12px",
          svg: {
            height: "24px",
            width: "24px",
          },
        },
        body: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "#fff",
          borderRadius: "50px 50px 12px 12px",
          padding: "0 40px 40px 40px",
        },
      }}
    >
      <Box mt="-32px">
        <InfoCircle size="60" color={theme.colors.primary[1]} variant="Bold" />
      </Box>
      <Text
        fw={600}
        fz={"32px"}
        lh="32px"
        maw={420}
        ta="center"
        style={{ wordBreak: "break-word", whiteSpace: "wrap" }}
      >
        {t("global.predictives_buy_card.modal_confirm.title")}
      </Text>
      <TextHTML
        maw={420}
        ta="center"
        fw={600}
        fz="16px"
        lh="24px"
        color="basic.4"
        mt="32px"
        mb="32px"
        textHTML={t("global.predictives_buy_card.modal_confirm.subtitle")}
      />
      <Flex direction="column" gap="8px" w="100%" mb="16px">
        <Text
          w={"100%"}
          style={{
            color: theme.colors.complementary[3],
            background: theme.colors.complementaryOpacity[3],
            padding: "0.4rem 0.7rem",
            borderRadius: "0.5rem",
            fontSize: "14px",
            fontWeight: 600,
            textAlign: "center",
            whiteSpace: "nowrap",
            flex: "1",
          }}
        >
          {meetingDate({
            date: data?.expiration || "",
            category: data?.category || "",
            t,
          })}
        </Text>
        <Text
          w={"100%"}
          style={{
            color: theme.colors.complementary[7],
            background: theme.colors.complementaryOpacity[7],
            padding: "0.4rem 0.7rem",
            borderRadius: "0.5rem",
            fontSize: "14px",
            fontWeight: 600,
            textAlign: "center",
            flex: "1",
          }}
        >
          {selectedInstrument?.label}
        </Text>
        <Text
          w={"100%"}
          style={{
            color: isSideSell
              ? theme.colors.complementary[4]
              : theme.colors.complementary[6],
            background: isSideSell
              ? theme.colors.complementaryOpacity[4]
              : theme.colors.complementaryOpacity[6],
            padding: "0.4rem 0.7rem",
            borderRadius: "0.5rem",
            fontSize: "14px",
            fontWeight: 600,
            textAlign: "center",
            flex: "1",
          }}
        >
          {isSideSell
            ? `${t("global.predictives.no")}   ${
                selectedInstrument?.calculated_ask_formatted ||
                formatCurrency(0)
              }`
            : `${t("global.predictives.yes")}   ${
                selectedInstrument?.level1_formatted?.ask_price ||
                formatCurrency(0)
              }`}
        </Text>
        <Text
          w={"100%"}
          style={{
            color: theme.colors.alerts[1],
            background: theme.colors.alertsOpacity[1],

            padding: "0.4rem 0.7rem",
            borderRadius: "0.5rem",
            fontSize: "14px",
            fontWeight: 600,
            textAlign: "center",
            flex: "1",
          }}
        >
          {t("global.predictives_buy_card.modal_confirm.qty_contract", {
            qty: watch.quantity,
          })}
        </Text>
        {isLimit ? (
          <>
            <Text
              w={"100%"}
              style={{
                color: theme.colors.complementary[2],
                background: theme.colors.complementaryOpacity[2],
                padding: "0.4rem 0.7rem",
                borderRadius: "0.5rem",
                fontSize: "14px",
                fontWeight: 600,
                textAlign: "center",
                flex: "1",
              }}
            >
              {t("global.predictives_buy_card.modal_confirm.limit_value", {
                value: formatCurrency(watch.price),
              })}
            </Text>
            <Text
              w={"100%"}
              style={{
                color: theme.colors.complementary[5],
                background: theme.colors.complementaryOpacity[5],
                padding: "0.4rem 0.7rem",
                borderRadius: "0.5rem",
                fontSize: "14px",
                fontWeight: 600,
                textAlign: "center",
                flex: "1",
              }}
            >
              {t("global.predictives_buy_card.modal_confirm.date", {
                date: dateExpiration,
              })}
            </Text>
          </>
        ) : null}
        <Text
          w={"100%"}
          style={{
            color: theme.colors.complementary[0],
            background: theme.colors.complementaryOpacity[0],
            padding: "0.4rem 0.7rem",
            borderRadius: "0.5rem",
            fontSize: "14px",
            fontWeight: 600,
            textAlign: "center",
            flex: "1",
          }}
        >
          {t("global.predictives_buy_card.modal_confirm.total_transaction", {
            value: formatCurrency(valueTransaction),
          })}
        </Text>
      </Flex>
      <Flex gap="16px" direction={"column"} w="100%" h="100%">
        <Button variant="primary" fullWidth onClick={onConfirm}>
          {t("global.predictives_buy_card.send_order")}
        </Button>
        <Button variant="primaryOutline" fullWidth onClick={onClose}>
          {t("global.predictives_buy_card.modal_confirm.modify")}
        </Button>
      </Flex>
    </Modal>
  );
}
